import Dialog from "@mui/material/Dialog";
import { ApplicationScreen, Doctor } from "../../models";
import CircularProgress from "@mui/material/CircularProgress";
import { useState } from "react";
import { apiAccessRequest } from "../../constants";
import { BuildUrl } from "../../helpers";
import { useUserContext } from "../../contexts";
import { useMsal } from "@azure/msal-react";
import { useCreateAuditLog } from "../../hooks";

interface ConfirmLogoutModalProps {
	doctor: Doctor;
	open: boolean;
	close: (loggedout: boolean) => void;
}

export const ConfirmLogoutModal = ({
	doctor,
	open,
	close
}: ConfirmLogoutModalProps) => {
	const { account } = useUserContext();
	const { instance } = useMsal();
	const [isError, setIsError] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const { createAuditLog } = useCreateAuditLog();

	const callCreateAuditLog = async () => {
		try {
			await createAuditLog([
				{
					doctorId: doctor.id,
					doctorName: doctor.fullName.trim(),
					username: account?.name ?? "",
					applicationScreen: ApplicationScreen.AdminTelehealthDoctor,
					action: "Logged out doctor"
				}
			]);
		} catch (error) {
			console.log("Error creating audit log", error);
		}
	};

	const handleConfirmClick = async () => {
		setIsError(false);
		setIsLoading(true);
		try {
			const requestUrl = BuildUrl(`doctor/${doctor?.id}/TelehealthLogout`);

			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				Authorization: `Bearer ${tokenResponse.accessToken}`,
				"Content-Type": "application/json"
			};

			const payload = {
				availableTeleheath: false,
				takingPatientsTelehealth: false
			};

			const response = await fetch(requestUrl, {
				method: "POST",
				headers,
				body: JSON.stringify(payload)
			});

			if (!response.ok) {
				setIsError(true);
				return;
			}
			close(true);
			await callCreateAuditLog();
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (error) {
			setIsError(true);
		}

		setIsLoading(false);
	};

	return (
		<Dialog open={open}>
			<div
				className="flex flex-col items-center p-5 gap-4"
				style={{ width: 420 }}
			>
				{!isError && (
					<>
						<p className="ch-m-text ch-em-text-navy text-center">
							{`Logout ${doctor?.salutation} ${doctor?.displayName}`}
						</p>
						<p className="pb-3 text-center">
							{`Are you sure you want to logout ${doctor?.salutation} ${doctor?.displayName}.`}
						</p>

						<div className="flex justify-end w-full mt-6 gap-4">
							{!isLoading && (
								<>
									<button
										className="ch-button ch-button-primary justify-center"
										style={{ width: 280 }}
										onClick={() => close(false)}
									>
										Go Back
									</button>
									<button
										className="ch-button ch-button-secondary justify-center"
										style={{ width: 280 }}
										onClick={() => handleConfirmClick()}
									>
										Yes, Logout
									</button>
								</>
							)}
							{isLoading && (
								<div className="flex justify-center py-3">
									<CircularProgress size="80px" />
								</div>
							)}
						</div>
					</>
				)}
				{isError && (
					<div className="flex flex-col items-center py-3">
						<img
							src="/images/svg/road-sign-warning.svg"
							height="50"
							alt="error icon"
						/>
						<p className="ch-m-text py-3 text-center">
							An error has occurred. The doctor has not been logged out. Try
							again later
						</p>
						<div className="flex gap-6">
							<button
								className="ch-button ch-button-primary"
								onClick={() => close(false)}
							>
								Go back
							</button>
						</div>
					</div>
				)}
			</div>
		</Dialog>
	);
};
